const campaignKey = "alfabetizando:marketing-attribution";
const lifetimeMs = 24 * 60 * 60 * 1000;
const campaignFields = ["src", "sck", "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
type Campaign = Partial<Record<typeof campaignFields[number], string>>;
interface SavedCampaign { readonly captured_at: number; readonly values: Campaign }
let current: SavedCampaign | null = null;
let lastTouch: string | null = null;
let touchInitialized = false;
let memoryOnly = false;

function cleanValue(value: unknown): string | null {
  if (typeof value !== "string" || Array.from(value).some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) return null;
  return value.trim().slice(0, 256) || null;
}
export function clearMarketingCampaign(): void {
  current = null; lastTouch = null; touchInitialized = false;
  try { localStorage.removeItem(campaignKey); memoryOnly = false; } catch { memoryOnly = true; }
  try { sessionStorage.removeItem(campaignKey); } catch { /* Remove legacy tab attribution when available. */ }
}
function discardStoredCampaign(): void {
  const previousTouch = lastTouch;
  const initialized = touchInitialized;
  clearMarketingCampaign(); lastTouch = previousTouch; touchInitialized = initialized;
}
function restoreCampaign(raw: string): SavedCampaign | null {
  if (raw.length > 4096) return null;
  let saved: unknown;
  try { saved = JSON.parse(raw); } catch { return null; }
  if (typeof saved !== "object" || saved === null || !("captured_at" in saved) || typeof saved.captured_at !== "number" ||
    !("values" in saved) || typeof saved.values !== "object" || saved.values === null || Array.isArray(saved.values)) return null;
  const values: Campaign = {};
  for (const field of campaignFields) {
    const value = cleanValue((saved.values as Record<string, unknown>)[field]);
    if (value) values[field] = value;
  }
  return Object.keys(values).length ? { captured_at: saved.captured_at, values } : null;
}
export function readMarketingCampaign(): Campaign {
  if (!memoryOnly) {
    try {
      let raw = localStorage.getItem(campaignKey);
      let legacy = false;
      if (raw === null) {
        try { raw = sessionStorage.getItem(campaignKey); legacy = raw !== null; } catch { /* Legacy migration is optional. */ }
      }
      current = raw === null ? null : restoreCampaign(raw);
      if (raw !== null && current === null) { discardStoredCampaign(); return {}; }
      if (current && (!Number.isFinite(current.captured_at) || current.captured_at <= 0 || current.captured_at > Date.now() || Date.now() - current.captured_at >= lifetimeMs)) {
        discardStoredCampaign(); return {};
      }
      if (raw !== null && (legacy || JSON.stringify(current) !== raw)) {
        try { localStorage.setItem(campaignKey, JSON.stringify(current)); } catch { memoryOnly = true; }
      }
      if (legacy && !memoryOnly) {
        try { sessionStorage.removeItem(campaignKey); } catch { /* Shared attribution is already sanitized and saved. */ }
      }
    } catch { /* In-memory attribution remains available when shared storage fails. */ }
  }
  if (!current) return {};
  if (!Number.isFinite(current.captured_at) || current.captured_at <= 0 || current.captured_at > Date.now() || Date.now() - current.captured_at >= lifetimeMs) {
    discardStoredCampaign(); return {};
  }
  return { ...current.values };
}
export function captureMarketingCampaign(input: string): void {
  let url: URL;
  try { url = new URL(input, window.location.origin); } catch { return; }
  if (url.origin !== window.location.origin) return;
  const values: Campaign = {};
  for (const field of campaignFields) {
    const value = cleanValue(url.searchParams.get(field));
    if (value) values[field] = value;
  }
  if (Object.keys(values).length === 0) {
    if (campaignFields.some((field) => url.searchParams.has(field))) clearMarketingCampaign();
    else { lastTouch = null; touchInitialized = true; }
    return;
  }
  const touch = JSON.stringify(values);
  if (!touchInitialized) {
    const restored = readMarketingCampaign();
    touchInitialized = true;
    // Reloading the same campaign URL must retain its original expiration.
    if (current && touch === JSON.stringify(restored)) { lastTouch = touch; return; }
  }
  if (touch === lastTouch) return;
  lastTouch = touch;
  current = { captured_at: Date.now(), values };
  try { localStorage.setItem(campaignKey, JSON.stringify(current)); memoryOnly = false; } catch { memoryOnly = true; }
  try { sessionStorage.removeItem(campaignKey); } catch { /* No duplicate legacy copy is needed. */ }
}
